import React, { useState } from 'react';
import { Alert, Button, Modal, Space, Steps, Table, Tag, Typography, Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { catalogImportMaster } from '../api';
import type { CatalogMasterImportResult } from '../api';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: (imported: boolean) => void;
}

/**
 * Parts Catalog ← Master Items sheet (xlsx exported from the part-number app).
 * Step 1 upload → server dry run → Step 2 review → commit → Step 3 result.
 */
const MasterSheetImportModal: React.FC<Props> = ({ open, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<CatalogMasterImportResult | null>(null);
  const [result, setResult] = useState<CatalogMasterImportResult | null>(null);

  const reset = () => { setFile(null); setStep(0); setBusy(false); setPreview(null); setResult(null); };
  const close = (imported: boolean) => { reset(); onClose(imported); };

  const run = async (commit: boolean) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await catalogImportMaster(file, commit);
      if (commit) { setResult(r); setStep(2); message.success(`Imported: ${r.added.length} added, ${r.updated.length} updated`); }
      else { setPreview(r); setStep(1); }
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const summary = (r: CatalogMasterImportResult) => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space wrap>
        <Tag>{r.totalRows} rows</Tag>
        <Tag color="green">{r.added.length} new</Tag>
        <Tag color="blue">{r.updated.length} to update</Tag>
        <Tag>{r.unchanged} unchanged</Tag>
        <Tag color={r.skipped.length ? 'red' : undefined}>{r.skipped.length} skipped</Tag>
        <Tag color={r.warnings.length ? 'orange' : undefined}>{r.warnings.length} warnings</Tag>
      </Space>
      {r.vendorPrices && (
        <Alert type="info" showIcon message="Supplier prices (full workbook)"
          description={`${r.vendorPrices.rows} price rows · ${r.vendorPrices.added} saved · cheapest vendor becomes the default price on ${r.vendorPrices.defaultsChanged} items${r.vendorPrices.vendorsCreated.length ? ` · new vendors created as Pending: ${r.vendorPrices.vendorsCreated.join(', ')}` : ''}${Object.keys(r.vendorPrices.unmappedVendorCodes).length ? ` · unmapped vendor codes: ${Object.keys(r.vendorPrices.unmappedVendorCodes).join(', ')}` : ''}${r.altUnits ? ` · alternative units: ${r.altUnits.applied}/${r.altUnits.rows}` : ''}`} />
      )}
      {Object.keys(r.unmappedUnits).length > 0 && (
        <Alert type="warning" showIcon message="Units in the sheet with no matching ERP unit (left empty)"
          description={Object.entries(r.unmappedUnits).map(([u, n]) => `${u} (${n})`).join(', ')} />
      )}
      {Object.keys(r.unmappedVendors).length > 0 && (
        <Alert type="warning" showIcon message="Suppliers in the sheet with no matching ERP vendor (left empty)"
          description={Object.entries(r.unmappedVendors).map(([v, n]) => `${v} (${n})`).join(', ')} />
      )}
      {r.skipped.length > 0 && (
        <Table size="small" pagination={{ pageSize: 5 }} rowKey={(x) => `${x.row}`} dataSource={r.skipped}
          title={() => <Text strong>Skipped rows</Text>}
          columns={[{ title: 'Row', dataIndex: 'row', width: 70 }, { title: 'Code', dataIndex: 'code', width: 260 }, { title: 'Reason', dataIndex: 'reason' }]} />
      )}
      {r.warnings.length > 0 && (
        <Table size="small" pagination={{ pageSize: 5 }} rowKey={(x, i) => `${x.row}-${i}`} dataSource={r.warnings}
          title={() => <Text strong>Warnings (rows still imported)</Text>}
          columns={[{ title: 'Row', dataIndex: 'row', width: 70 }, { title: 'Code', dataIndex: 'code', width: 260 }, { title: 'Message', dataIndex: 'message' }]} />
      )}
      {r.updated.length > 0 && (
        <Table size="small" pagination={{ pageSize: 5 }} rowKey="code" dataSource={r.updated}
          title={() => <Text strong>Existing codes that will change</Text>}
          columns={[{ title: 'Code', dataIndex: 'code', width: 260 }, { title: 'Fields', dataIndex: 'changes', render: (c: string[]) => c.join(', ') }]} />
      )}
      {r.added.length > 0 && (
        <Table size="small" pagination={{ pageSize: 5 }} rowKey="code" dataSource={r.added}
          title={() => <Text strong>New codes</Text>}
          columns={[{ title: 'Code', dataIndex: 'code', width: 260 }, { title: 'Description', dataIndex: 'description', ellipsis: true }]} />
      )}
    </Space>
  );

  return (
    <Modal
      open={open}
      title="Import Master Items sheet"
      width={900}
      onCancel={() => close(step === 2)}
      destroyOnClose
      footer={
        step === 0 ? [
          <Button key="cancel" onClick={() => close(false)}>Cancel</Button>,
          <Button key="check" type="primary" disabled={!file} loading={busy} onClick={() => run(false)}>Check sheet</Button>,
        ] : step === 1 ? [
          <Button key="back" onClick={() => { setStep(0); setPreview(null); }}>Back</Button>,
          <Button key="cancel" onClick={() => close(false)}>Cancel</Button>,
          <Button key="commit" type="primary" loading={busy}
            disabled={!preview || (preview.added.length === 0 && preview.updated.length === 0)}
            onClick={() => run(true)}>
            Import {preview ? preview.added.length + preview.updated.length : 0} items
          </Button>,
        ] : [
          <Button key="done" type="primary" onClick={() => close(true)}>Done</Button>,
        ]
      }
    >
      <Steps current={step} size="small" style={{ marginBottom: 24 }}
        items={[{ title: 'Upload .xlsx' }, { title: 'Review (dry run)' }, { title: 'Result' }]} />

      {step === 0 && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert type="info" showIcon message="Expected sheet"
            description='The "Master Items" export from the part-number app: Code, Class, Type, Sub-class, Sub-type, Description (EN), Description (AR), Department, Unit, Created. Nothing is written until you confirm on the next step. Existing codes are never deactivated.' />
          <Upload.Dragger
            accept=".xlsx"
            maxCount={1}
            beforeUpload={(f) => { setFile(f); return false; }}
            onRemove={() => setFile(null)}
            fileList={file ? [{ uid: '1', name: file.name, status: 'done' as const }] : []}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag the Master Items .xlsx here</p>
          </Upload.Dragger>
        </Space>
      )}
      {step === 1 && preview && summary(preview)}
      {step === 2 && result && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert type="success" showIcon message={`Imported ${result.added.length} new and updated ${result.updated.length} existing catalog items.`} />
          {summary(result)}
        </Space>
      )}
    </Modal>
  );
};

export default MasterSheetImportModal;
